/**
 * Talking to the backend.
 *
 * Every request in the app goes through here, which is what makes three
 * things possible in one place rather than sixty-eight.
 *
 * TOKENS REFRESH THEMSELVES
 * -------------------------
 * A login lasts 24 hours, and a cashier's shift can outlast it. Being thrown
 * back to a login screen mid-order, with a customer waiting and a cart that
 * disappears, is not acceptable — so a 401 triggers a refresh and the original
 * request is retried once, invisibly.
 *
 * Only once. A refresh that itself fails means the session is genuinely over,
 * and retrying again would loop.
 *
 * ONE REFRESH AT A TIME
 * ---------------------
 * A screen that loads six things at once gets six 401s together. Without care
 * that is six refresh calls, five of which use a token the first has already
 * replaced — so five fail and the user is logged out anyway. The first refresh
 * is shared; the rest wait for it.
 *
 * OFFLINE IS NOT AN ERROR
 * -----------------------
 * The branch machine is on the same local network as the till, so "offline"
 * here means that machine, not the internet. It is still worth telling apart
 * from a genuine fault, because the two need different words on screen.
 */

/** Where the API lives. Empty means same origin, which the dev proxy handles. */
const BASE = import.meta.env.VITE_API_BASE ?? '';

/** Thrown for anything the caller might reasonably want to inspect. */
export class ApiError extends Error {
  constructor(message, { status, code, details, offline = false } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.offline = offline;
  }

  /** Did the request fail because of something the user typed? */
  get isValidation() {
    return this.status === 400 || this.status === 422;
  }

  /** Is this someone's session ending, rather than a fault? */
  get isAuth() {
    return this.status === 401 || this.status === 403;
  }
}

/* ------------------------------------------------------------------ tokens */

const ACCESS_KEY = 'foodrun.token';
const REFRESH_KEY = 'foodrun.refreshToken';

/**
 * Tokens live in localStorage.
 *
 * A till is a shared machine that gets closed and reopened, and staff should
 * not have to sign in again because a tab was refreshed. The alternative —
 * keeping them in memory only — is more resistant to a script reading them,
 * but this app serves no third-party scripts and the cost falls on the people
 * using it every day.
 *
 * Wrapped because a browser in private mode can refuse storage outright, and
 * that should degrade to "log in again", not a blank screen.
 */
export const tokens = {
  get access() {
    try {
      return localStorage.getItem(ACCESS_KEY);
    } catch {
      return null;
    }
  },

  get refresh() {
    try {
      return localStorage.getItem(REFRESH_KEY);
    } catch {
      return null;
    }
  },

  set({ token, refreshToken }) {
    try {
      if (token) localStorage.setItem(ACCESS_KEY, token);
      if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    } catch {
      /* private mode — the session lasts until the tab closes */
    }
  },

  clear() {
    try {
      localStorage.removeItem(ACCESS_KEY);
      localStorage.removeItem(REFRESH_KEY);
    } catch {
      /* nothing to clear */
    }
  },
};

/* ----------------------------------------------------------------- refresh */

/** The refresh in progress, shared by everything waiting on it. */
let refreshing = null;

/** Called when the session is over for good, so the app can react once. */
let onSessionEnded = () => {};

export function setSessionEndedHandler(fn) {
  onSessionEnded = fn;
}

async function refreshSession() {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  const response = await fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) return false;

  const data = await response.json().catch(() => null);
  if (!data?.token) return false;

  tokens.set(data);
  return true;
}

/** Refresh, or join the refresh already running. */
function refreshOnce() {
  refreshing ??= refreshSession().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/* ---------------------------------------------------------------- requests */

async function send(method, path, { body, signal, headers = {} } = {}) {
  const token = tokens.access;

  // A file upload sends FormData, and the browser writes its own
  // Content-Type with the multipart boundary — setting one by hand, or
  // JSON-stringifying it, both break the upload. Everything else is JSON.
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  let response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      method,
      signal,
      headers: {
        ...(body === undefined || isForm ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError('Cannot reach the branch computer.', { offline: true });
  }

  return response;
}

async function parse(response) {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    // An HTML error page, or a proxy that intercepted the call.
    throw new ApiError('The server sent something unexpected.', {
      status: response.status,
    });
  }
}

/**
 * Make a request, refreshing the session once if it has expired.
 *
 * @param {string} method
 * @param {string} path    starting with /, e.g. '/pos/orders'
 * @param {object} [options]
 */
export async function request(method, path, options = {}) {
  let response = await send(method, path, options);

  // Expired token: refresh and try the original request again, once.
  if (response.status === 401 && tokens.refresh) {
    const recovered = await refreshOnce();

    if (!recovered) {
      tokens.clear();
      onSessionEnded();
      throw new ApiError('Your session has ended. Please sign in again.', { status: 401 });
    }

    response = await send(method, path, options);
  }

  const data = await parse(response);

  if (!response.ok) {
    if (response.status === 401) {
      tokens.clear();
      onSessionEnded();
    }

    throw new ApiError(data?.error || `Request failed (${response.status})`, {
      status: response.status,
      code: data?.code,
      details: data?.details,
    });
  }

  return data;
}

export const api = {
  get: (path, options) => request('GET', path, options),
  post: (path, body, options) => request('POST', path, { ...options, body }),
  // A file. Hand it a FormData and it goes up as multipart, session refresh
  // and all, rather than being turned into JSON.
  upload: (path, formData, options) => request('POST', path, { ...options, body: formData }),
  put: (path, body, options) => request('PUT', path, { ...options, body }),
  patch: (path, body, options) => request('PATCH', path, { ...options, body }),
  delete: (path, options) => request('DELETE', path, options),
};

/**
 * Build a path with a query string, dropping anything unset.
 *
 * Without the dropping, an unset filter becomes ?branch_id=undefined and the
 * backend filters on the string "undefined", returning nothing at all.
 */
export function withQuery(path, params = {}) {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }

  const query = search.toString();
  return query ? `${path}?${query}` : path;
}
